import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'

const ACCESS_KEY = 'caion_app_access'
const REFRESH_KEY = 'caion_app_refresh'
const KEEP_LOGIN_KEY = 'caion_app_keep_login'

/**
 * 로그인 유지 여부 — false면 토큰을 sessionStorage에 두어
 * 브라우저 세션이 끝나면 자동으로 로그아웃된다. 기본값은 유지(true).
 */
export function getKeepLogin(): boolean {
  return localStorage.getItem(KEEP_LOGIN_KEY) !== '0'
}

export function setKeepLogin(v: boolean) {
  localStorage.setItem(KEEP_LOGIN_KEY, v ? '1' : '0')
}

export function getAccessToken(): string {
  return localStorage.getItem(ACCESS_KEY) || sessionStorage.getItem(ACCESS_KEY) || ''
}

/** img src용 — Authorization 헤더가 안 붙을 때. referrerpolicy="no-referrer" 와 함께 사용 */
export function appAuthImageUrl(path: string): string {
  const token = getAccessToken()
  if (!token) return path
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}token=${encodeURIComponent(token)}`
}

export function getRefreshToken(): string {
  return localStorage.getItem(REFRESH_KEY) || sessionStorage.getItem(REFRESH_KEY) || ''
}

export function saveTokens(access: string, refresh: string) {
  const keep = getKeepLogin()
  const primary = keep ? localStorage : sessionStorage
  const secondary = keep ? sessionStorage : localStorage
  secondary.removeItem(ACCESS_KEY)
  secondary.removeItem(REFRESH_KEY)
  primary.setItem(ACCESS_KEY, access)
  primary.setItem(REFRESH_KEY, refresh)
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY)
  localStorage.removeItem(REFRESH_KEY)
  sessionStorage.removeItem(ACCESS_KEY)
  sessionStorage.removeItem(REFRESH_KEY)
}

/**
 * 로그아웃·세션 만료 시 증가.
 * 진행 중 refreshMe / refreshAccessToken 이 완료되어도 예전 epoch면 결과를 버린다.
 */
let sessionEpoch = 0

export function getSessionEpoch() {
  return sessionEpoch
}

/** 로그아웃·강제 세션 종료 — 진행 중 토큰 갱신 결과를 폐기 */
export function invalidateSession() {
  sessionEpoch += 1
}

/** refresh 실패 시 authStore.user 동기화용 */
let onSessionCleared: (() => void) | null = null

export function setOnSessionCleared(cb: (() => void) | null) {
  onSessionCleared = cb
}

function clearSessionLocal() {
  invalidateSession()
  clearTokens()
  onSessionCleared?.()
}

/** HTTP 상태 → 사용자용 안내 (상태코드 숫자 노출 금지) */
const HTTP_USER_MESSAGE: Record<number, string> = {
  400: '입력 내용을 확인해 주세요.',
  401: '로그인이 필요하거나 인증이 만료되었습니다.',
  403: '권한이 없습니다.',
  404: '요청한 정보를 찾을 수 없습니다.',
  408: '요청 시간이 초과되었습니다. 잠시 후 다시 시도해 주세요.',
  429: '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.',
  500: '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
  502: '서버에 일시적으로 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.',
  503: '서비스를 잠시 사용할 수 없습니다. 잠시 후 다시 시도해 주세요.',
  504: '서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.',
}

const DEFAULT_USER_MESSAGE = '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.'

/** 서버/브라우저 원문이 사용자에게 보여줄 수 있는지 */
const isUserFacingMessage = (msg: string): boolean => {
  const s = String(msg || '').trim()
  if (!s || s.length > 180) return false
  if (/status code\s*\d+/i.test(s)) return false
  if (/Request failed/i.test(s)) return false
  if (/Network Error/i.test(s)) return false
  if (/timeout/i.test(s) && /exceeded|ms/i.test(s)) return false
  if (/ECONNREFUSED|ETIMEDOUT|ENOTFOUND|ECONNRESET/i.test(s)) return false
  if (/<!DOCTYPE|<html|Bad Gateway|Service Unavailable|Gateway Time-out/i.test(s)) return false
  if (/^\d{3}$/.test(s)) return false
  return true
}

const sanitizeMessage = (raw: unknown): string | null => {
  if (raw == null) return null
  const s = String(raw).trim()
  if (!isUserFacingMessage(s)) return null
  return s
}

/**
 * API/네트워크 오류 → 사용자용 문구만 반환.
 * 상태코드·axios 기본 영문 메시지는 노출하지 않음.
 */
export function apiErrorMessage(e: unknown, fallback = DEFAULT_USER_MESSAGE): string {
  if (axios.isAxiosError(e)) {
    const err = e as AxiosError<{ message?: string; error?: string }>
    const status = err.response?.status
    const fromBody =
      sanitizeMessage(err.response?.data?.message) ||
      sanitizeMessage((err.response?.data as any)?.error)

    if (fromBody) return fromBody

    if (!err.response) {
      if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
        return '요청 시간이 초과되었습니다. 잠시 후 다시 시도해 주세요.'
      }
      return '네트워크 연결을 확인해 주세요.'
    }

    if (status && HTTP_USER_MESSAGE[status]) return HTTP_USER_MESSAGE[status]
    if (status && status >= 500) return HTTP_USER_MESSAGE[500]
    if (status && status >= 400) return HTTP_USER_MESSAGE[400]
  }

  if (e instanceof Error) {
    const cleaned = sanitizeMessage(e.message)
    if (cleaned) return cleaned
  }

  if (typeof e === 'string') {
    const cleaned = sanitizeMessage(e)
    if (cleaned) return cleaned
  }

  return fallback || DEFAULT_USER_MESSAGE
}

/** 기술/서버 오류 → 문의 유도 대상 (검증·권한 안내는 제외) */
function isReportableAppError(e: unknown): boolean {
  if (e === undefined || e === null) return true
  if (axios.isAxiosError(e)) {
    const status = e.response?.status
    const raw = String(
      (e.response?.data as any)?.message ||
        (e.response?.data as any)?.error ||
        e.message ||
        ''
    )
    if (!e.response) return true
    if (status != null && status >= 500) return true
    if (/is not a function|Cannot read|undefined is not|ECONN|ETIMEDOUT|ENOTFOUND|ER_|SQLSYNTAX|Unexpected token/i.test(raw)) {
      return true
    }
    // 400/403/404 등 업무 안내 메시지는 그대로 표시
    if (status === 400 || status === 403 || status === 404 || status === 409 || status === 429) {
      return false
    }
    if (status === 401) return false
    return true
  }
  if (e instanceof Error) {
    if (/is not a function|Cannot read|undefined is not/i.test(e.message || '')) return true
    // Error(message)로 던진 업무 안내
    if (isUserFacingMessage(e.message || '')) return false
    return true
  }
  if (typeof e === 'string') {
    // API 실패 시 res.message 문자열 — 한글 업무 안내는 그대로 표시
    if (/is not a function|Cannot read|undefined is not|ECONN|ER_/i.test(e)) return true
    if (isUserFacingMessage(e)) return false
    return true
  }
  return true
}

function extractTechErrorDetail(e: unknown): { message: string; status?: number } {
  if (axios.isAxiosError(e)) {
    const status = e.response?.status
    const bodyMsg = String(
      (e.response?.data as any)?.message ||
        (e.response?.data as any)?.error ||
        ''
    ).trim()
    const msg = bodyMsg || e.message || 'unknown'
    return { message: msg.slice(0, 1500), status }
  }
  if (e instanceof Error) return { message: (e.message || 'Error').slice(0, 1500) }
  if (typeof e === 'string') return { message: e.slice(0, 1500) }
  try {
    return { message: JSON.stringify(e).slice(0, 1500) }
  } catch {
    return { message: 'unknown' }
  }
}

/** 로그아웃·세션 종료 직후 인플라이트 API 실패 — 알림 불필요 */
function isLoggedOutAuthNoise(e: unknown): boolean {
  // 액세스·리프레시 모두 없으면 로그아웃(또는 강제 세션 종료) 상태로 간주
  const loggedOut = !getAccessToken() && !getRefreshToken()
  if (!loggedOut) return false

  if (axios.isAxiosError(e)) {
    const status = e.response?.status
    if (status === 401 || status === 403) return true
  }
  const raw =
    (axios.isAxiosError(e)
      ? String((e.response?.data as any)?.message || e.message || '')
      : e instanceof Error
        ? e.message
        : typeof e === 'string'
          ? e
          : '') || ''
  if (!raw.trim()) return true
  return /인증|토큰|세션이 종료|로그인이 필요|만료/.test(raw)
}

/**
 * 통일 오류 처리
 * - 업무/검증 오류: 짧은 안내만 표시
 * - 시스템·예상치 못한 오류: 메시지 숨기고 문의 남길지 확인 → 자동 문의 등록
 */
export async function alertError(action: string, e?: unknown): Promise<void> {
  // 로그아웃 후 폴링·잔여 요청이 401/토큰 오류를 내도 사용자에게 알리지 않음
  if (isLoggedOutAuthNoise(e)) return

  const { appAlert, appConfirm } = await import('@/composables/useAppConfirm')
  const actionLabel = String(action || '').trim() || '작업'

  // 문의 등록 자체 실패 시 재귀 방지
  const skipInquiry =
    /문의/.test(actionLabel) ||
    (typeof window !== 'undefined' && (window as any).__caionInquiryReporting)

  if (!isReportableAppError(e) || skipInquiry) {
    const detail = e === undefined || e === null ? DEFAULT_USER_MESSAGE : apiErrorMessage(e)
    if (!detail || detail === actionLabel || detail.startsWith(`${actionLabel}:`)) {
      await appAlert({ message: detail || actionLabel })
      return
    }
    await appAlert({ message: `${actionLabel}: ${detail}` })
    return
  }

  const ok = await appConfirm({
    title: '오류 발생',
    message:
      '일시적인 오류가 발생했습니다.\n이 오류에 대해 문의사항을 남기시겠습니까?\n(남기시면 제목·내용이 자동으로 작성됩니다.)',
    confirmText: '문의 남기기',
    cancelText: '닫기',
  })
  if (!ok) return

  if (!getAccessToken()) {
    await appAlert({
      message: '문의 등록에는 로그인이 필요합니다. 로그인 후 문의상담 메뉴를 이용해 주세요.',
    })
    return
  }

  const tech = extractTechErrorDetail(e)
  const pagePath =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search || ''}`
      : ''

  ;(window as any).__caionInquiryReporting = true
  try {
    const res = await appApi.post('/inquiry/error-report', {
      action: actionLabel,
      message: tech.message,
      status: tech.status ?? null,
      path: pagePath,
    })
    if (res.success) {
      await appAlert({
        message: '오류 문의가 접수되었습니다. 문의상담 메뉴에서 확인할 수 있습니다.',
      })
    } else {
      await appAlert({
        message: '문의 접수에 실패했습니다. 잠시 후 문의상담 메뉴에서 직접 남겨 주세요.',
      })
    }
  } catch {
    await appAlert({
      message: '문의 접수에 실패했습니다. 잠시 후 문의상담 메뉴에서 직접 남겨 주세요.',
    })
  } finally {
    ;(window as any).__caionInquiryReporting = false
  }
}

export function apiRetryAfterSec(e: unknown): number | undefined {
  if (!axios.isAxiosError(e)) return undefined
  const sec = (e.response?.data as any)?.data?.retryAfterSec
  return sec != null ? Number(sec) : undefined
}

/** 로그인·토큰 갱신 등 — 401 시 refresh 재시도 금지 */
const skipRefreshRetry = (url?: string) => {
  const u = String(url || '')
  return (
    u.includes('/auth/refresh') ||
    u.includes('/auth/logout') ||
    u.includes('/auth/login') ||
    u.includes('/auth/register') ||
    u.includes('/auth/otp') ||
    u.includes('/auth/reset-password') ||
    u.includes('/auth/check-id')
  )
}

let refreshing: Promise<{ ok: boolean; data?: Record<string, any> }> | null = null

/**
 * refresh 토큰 1회 회전 — bootstrap·401 인터셉터가 동일 Promise를 공유해야
 * 로테이션 레이스로 방금 발급된 토큰이 지워지지 않음.
 */
export async function refreshAccessToken(): Promise<{ ok: boolean; data?: Record<string, any> }> {
  const refresh = getRefreshToken()
  if (!refresh) return { ok: false }
  const epochAtStart = sessionEpoch
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const { data } = await axios.post(
          '/api/app/auth/refresh',
          { refreshToken: refresh },
          {
            timeout: 15000,
            headers: { 'Content-Type': 'application/json' },
          }
        )
        // 로그아웃이 끼어들면 새 토큰을 저장하지 않고 서버에서 폐기
        if (sessionEpoch !== epochAtStart) {
          const issued = data?.data?.refreshToken
          if (issued) {
            void axios
              .post(
                '/api/app/auth/logout',
                { refreshToken: issued },
                { timeout: 8000, headers: { 'Content-Type': 'application/json' } }
              )
              .catch(() => {})
          }
          return { ok: false }
        }
        if (data?.success && data?.data?.accessToken) {
          const session = data.data as Record<string, any>
          saveTokens(String(session.accessToken), String(session.refreshToken || refresh))
          return { ok: true, data: session }
        }
        clearSessionLocal()
        return { ok: false }
      } catch {
        if (sessionEpoch === epochAtStart) clearSessionLocal()
        return { ok: false }
      } finally {
        refreshing = null
      }
    })()
  }
  return refreshing
}

const client: AxiosInstance = axios.create({
  baseURL: '/api/app',
  timeout: 30000,
  maxContentLength: 50 * 1024 * 1024,
  maxBodyLength: 50 * 1024 * 1024,
  headers: { 'Content-Type': 'application/json' },
})

client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken()
  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

client.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !skipRefreshRetry(original.url)
    ) {
      original._retry = true
      const { ok } = await refreshAccessToken()
      if (ok) {
        original.headers = original.headers || {}
        original.headers.Authorization = `Bearer ${getAccessToken()}`
        return client(original)
      }
    }
    return Promise.reject(error)
  }
)

export type ApiOk<T = any> = { success: boolean; message?: string; data?: T }

export const appApi = {
  get: async <T = any>(
    path: string,
    params?: Record<string, any>,
    opts?: { signal?: AbortSignal }
  ): Promise<ApiOk<T>> => {
    const { data } = await client.get(path, { params, signal: opts?.signal })
    return data
  },
  post: async <T = any>(
    path: string,
    body?: any,
    opts?: { signal?: AbortSignal; timeout?: number }
  ): Promise<ApiOk<T>> => {
    const { data } = await client.post(path, body, {
      signal: opts?.signal,
      timeout: opts?.timeout,
    })
    return data
  },
  put: async <T = any>(path: string, body?: any): Promise<ApiOk<T>> => {
    const { data } = await client.put(path, body)
    return data
  },
  delete: async <T = any>(path: string): Promise<ApiOk<T>> => {
    const { data } = await client.delete(path)
    return data
  },
  upload: async <T = any>(path: string, formData: FormData): Promise<ApiOk<T>> => {
    const { data } = await client.post(path, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  },
  /** PDF 등 binary 응답 */
  getBlob: async (path: string, params?: Record<string, any>): Promise<Blob> => {
    const { data } = await client.get(path, { params, responseType: 'blob' })
    return data as Blob
  },
  postBlob: async (path: string, body?: any): Promise<Blob> => {
    const { data } = await client.post(path, body, { responseType: 'blob' })
    return data as Blob
  },
  isAbortError: (e: unknown) =>
    axios.isCancel(e) ||
    (typeof e === 'object' &&
      e != null &&
      'code' in e &&
      ((e as { code?: string }).code === 'ERR_CANCELED' ||
        (e as { code?: string }).code === 'ECONNABORTED')) ||
    (typeof DOMException !== 'undefined' && e instanceof DOMException && e.name === 'AbortError'),
}

export default appApi
